import { Navigate, Outlet, RouteObject, useNavigate } from 'react-router-dom'
import {
    ECCOnBoardingTabNames,
    SchoolOnBoardingTabNames,
    TabNames,
} from './type'
import { lazy, useMemo } from 'react'
import { Stack } from '@mui/material'
import { useSelector } from 'react-redux'
import { RootState } from '../redux/store'
import DashboardHeader from '../components/Header/DashboardHeader'
import {
    selectUserAccountTypeCheck,
    selectUserAuthCheck,
    userCentresCountSelector,
    userSchoolsCountSelector,
} from '../redux/userSlice'
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner'
import { authMiddleware } from '../middleware'
import { logger } from '../helpers/logger'

const OnboardingRoute = () => {
    const auth = useSelector(selectUserAuthCheck)

    // NOTE: Turn off email verification
    // const emailVerified = useSelector(selectUserAuthEmailVerifiedCheck)

    const loading = auth === undefined
    // NOTE: Turn off email verification
    // || emailVerified === undefined

    if (loading) return <LoadingSpinner fillViewPortHeight fillViewPortWidth />

    if (auth === false) {
        logger.debug(`ONBOARDING_ROUTE: 1`)
        return <Navigate to={'/'} replace />
    }
    // NOTE: Turn off email verification
    // else if (process.env.NODE_ENV === 'production' && !emailVerified) {
    //     logger.debug(`ONBOARDING_ROUTE: 2`)
    //     return <Navigate to={`/${TabNames.VERIFY_EMAIL}`} replace />
    // }

    logger.debug(`ONBOARDING_ROUTE: 3`)
    return <OnboardingLayout />
}

const OnboardingLayout = () => {
    const schoolsCount = useSelector(userSchoolsCountSelector)
    const centresCount = useSelector(userCentresCountSelector)

    const showHeader: boolean = useMemo(() => {
        let hasSchools = Boolean(schoolsCount || 0)
        let hasCentres = Boolean(centresCount || 0)
        return hasCentres || hasSchools
    }, [schoolsCount, centresCount])

    return (
        <>
            {showHeader ? <DashboardHeader /> : null}
            <Stack
                id="layout-container"
                sx={{ height: '100dvh', width: '100vw', overflowY: 'auto' }}
                alignItems={'center'}
            >
                <Outlet />
            </Stack>
        </>
    )
}

const OnboardingPage = lazy(() =>
    import('../pages/OnBoardingPage').then((m) => ({ default: m.default }))
)

// School Onboarding
const PostSchoolSearchLayout = () => {
    const formData = useSelector((state: RootState) => state.schoolOnboarding)
    const navigate = useNavigate()

    if (formData.school === undefined) {
        if (document.startViewTransition) {
            document.startViewTransition(() =>
                navigate(`/${TabNames.ON_BOARDING}/school`)
            )
        } else {
            navigate(`/${TabNames.ON_BOARDING}/school`)
        }
    }
    return <Outlet />
}
const SchoolSelect = lazy(() =>
    import('../pages/OnBoardingPage/School/components/SchoolSelect').then(
        (m) => ({ default: m.default })
    )
)
const BasicDetails = lazy(() =>
    import('../pages/OnBoardingPage/School/components/BasicDetails').then(
        (m) => ({ default: m.default })
    )
)
const SchoolInformation = lazy(() =>
    import('../pages/OnBoardingPage/School/components/SchoolInformation').then(
        (m) => ({ default: m.default })
    )
)
const SchoolBellTimes = lazy(() =>
    import('../pages/OnBoardingPage/School/components/SchoolBellTimes').then(
        (m) => ({ default: m.default })
    )
)
const SchoolBMDetails = lazy(() =>
    import('../pages/OnBoardingPage/School/components/BMDetails').then((m) => ({
        default: m.default,
    }))
)
const SchoolOnboardingSubmit = lazy(() =>
    import('../pages/OnBoardingPage/School/components/SubmitRegistration').then(
        (m) => ({ default: m.default })
    )
)

// Centre Onboarding
const PostCentreSearchLayout = () => {
    const formData = useSelector((state: RootState) => state.eccOnboarding)
    const navigate = useNavigate()

    if (formData.centre === undefined) {
        if (document.startViewTransition) {
            document.startViewTransition(() =>
                navigate(`/${TabNames.ON_BOARDING}/ecc`)
            )
        } else {
            navigate(`/${TabNames.ON_BOARDING}/ecc`)
        }
    }
    return <Outlet />
}
const CentreSelect = lazy(() =>
    import('../pages/OnBoardingPage/ECC/components/CentreSelect').then((m) => ({
        default: m.default,
    }))
)
const CentreBasicDetails = lazy(() =>
    import('../pages/OnBoardingPage/ECC/components/BasicDetails').then((m) => ({
        default: m.default,
    }))
)
const CentreInformation = lazy(() =>
    import('../pages/OnBoardingPage/ECC/components/CentreInformation').then(
        (m) => ({ default: m.default })
    )
)
const CentreServices = lazy(() =>
    import('../pages/OnBoardingPage/ECC/components/Services').then((m) => ({
        default: m.default,
    }))
)
const CentreLanguages = lazy(() =>
    import('../pages/OnBoardingPage/ECC/components/Languages').then((m) => ({
        default: m.default,
    }))
)
const CentreAges = lazy(() =>
    import('../pages/OnBoardingPage/ECC/components/Ages').then((m) => ({
        default: m.default,
    }))
)
const CentreOpenTimes = lazy(() =>
    import('../pages/OnBoardingPage/ECC/components/OpenTimes').then((m) => ({
        default: m.default,
    }))
)
const CentreBusinessManager = lazy(() =>
    import('../pages/OnBoardingPage/ECC/components/BMDetails').then((m) => ({
        default: m.default,
    }))
)
const CentreOnboardingSubmit = lazy(() =>
    import('../pages/OnBoardingPage/ECC/components/SubmitRegistration').then(
        (m) => ({ default: m.default })
    )
)
const InstitutionOnboardingRoute = ({
    type,
}: {
    type: 'school' | 'childcareCentre'
}) => {
    const actorType = useSelector(selectUserAccountTypeCheck)
    const institutionType = useSelector(
        (state: RootState) => state.preRegistration.institutionType
    )

    if (actorType === undefined) return <LoadingSpinner />

    if (
        actorType === 'organisation' ||
        actorType === type ||
        institutionType === type
    ) {
        return <Outlet />
    } else {
        return <Navigate to={`/${TabNames.ON_BOARDING}`} replace />
        // return <p>Invalid Route</p>
    }
}

const onboardingRoutes: RouteObject = {
    path: 'onboarding',
    middleware: [authMiddleware],
    element: <OnboardingRoute />,
    children: [
        { index: true, element: <OnboardingPage /> },
        // {
        //     path: 'employee',
        //     children: [{ index: true, element: <AccountTypeSelection /> }],
        // },
        // {
        //     path: 'employer',
        //     children: [{ index: true, element: <AccountTypeSelection /> }],
        // },
        {
            path: 'school',
            element: <InstitutionOnboardingRoute type="school" />,
            children: [
                {
                    index: true,
                    element: <SchoolSelect />,
                    handle: {
                        meta: {
                            title: 'School Onboarding - Find Your School | Spark Relief Teachers',
                            description:
                                'Find your school and begin setting up your Spark Relief Teachers profile to start booking relief teachers.',
                        },
                    },
                },
                {
                    element: <PostSchoolSearchLayout />,
                    children: [
                        {
                            path: SchoolOnBoardingTabNames.BASIC_DETAILS,
                            element: <BasicDetails />,
                            handle: {
                                meta: {
                                    title: 'School Onboarding - Basic Details | Spark Relief Teachers',
                                    description:
                                        "Add your school's basic details so we can set up your Spark Relief Teachers onboarding profile correctly.",
                                },
                            },
                        },
                        {
                            path: SchoolOnBoardingTabNames.INFORMATION,
                            element: <SchoolInformation />,
                            handle: {
                                meta: {
                                    title: 'School Onboarding - School Information | Spark Relief Teachers',
                                    description:
                                        'Provide key information about your school to help complete your Spark Relief Teachers setup.',
                                },
                            },
                        },
                        {
                            path: SchoolOnBoardingTabNames.BELL_TIMES,
                            element: <SchoolBellTimes />,
                            handle: {
                                meta: {
                                    title: 'School Onboarding - Bell Times | Spark Relief Teachers',
                                    description:
                                        "Enter your school's bell times so relief bookings can be matched to your daily schedule.",
                                },
                            },
                        },
                        {
                            path: SchoolOnBoardingTabNames.BUSINESS_MANAGER,
                            element: <SchoolBMDetails />,
                            handle: {
                                meta: {
                                    title: 'School Onboarding - Business Manager Details | Spark Relief Teachers',
                                    description:
                                        'Add your business manager details to support communication and school account setup in Spark Relief Teachers.',
                                },
                            },
                        },
                        {
                            path: SchoolOnBoardingTabNames.SUBMIT,
                            element: <SchoolOnboardingSubmit />,
                            handle: {
                                meta: {
                                    title: 'School Onboarding - Review and Submit | Spark Relief Teachers',
                                    description:
                                        'Review your school onboarding details and submit your profile to finish setting up Spark Relief Teachers.',
                                },
                            },
                        },
                    ],
                },
            ],
            handle: {
                meta: {
                    title: 'School Onboarding | Spark Relief Teachers',
                    description:
                        'Complete your school profile to start booking qualified relief teachers through Spark Relief Teachers.',
                },
            },
        },
        {
            path: 'ecc',
            element: <InstitutionOnboardingRoute type={'childcareCentre'} />,
            children: [
                {
                    index: true,
                    element: <CentreSelect />,
                    handle: {
                        meta: {
                            title: 'Childcare Onboarding - Find Your Centre | Spark Relief Teachers',
                            description:
                                'Find your centre and begin setting up your Spark Relief Teachers profile to start booking early childhood educators.',
                        },
                    },
                },
                {
                    element: <PostCentreSearchLayout />,
                    children: [
                        {
                            path: ECCOnBoardingTabNames.BASIC_DETAILS,
                            element: <CentreBasicDetails />,
                            handle: {
                                meta: {
                                    title: 'Childcare Onboarding - Basic Details | Spark Relief Teachers',
                                    description:
                                        'Add your centre details so we can set up your Spark Relief Teachers onboarding profile correctly.',
                                },
                            },
                        },
                        {
                            path: ECCOnBoardingTabNames.INFORMATION,
                            element: <CentreInformation />,
                            handle: {
                                meta: {
                                    title: 'Childcare Onboarding - Centre Information | Spark Relief Teachers',
                                    description:
                                        'Provide key information about your centre to help complete your Spark Relief Teachers setup.',
                                },
                            },
                        },
                        {
                            path: ECCOnBoardingTabNames.SERVICES,
                            element: <CentreServices />,
                            handle: {
                                meta: {
                                    title: 'Childcare Onboarding - Services | Spark Relief Teachers',
                                    description:
                                        'Add your centre services so Spark Relief Teachers can better support your staffing needs.',
                                },
                            },
                        },
                        {
                            path: ECCOnBoardingTabNames.LANGUAGES,
                            element: <CentreLanguages />,
                            handle: {
                                meta: {
                                    title: 'Childcare Onboarding - Languages | Spark Relief Teachers',
                                    description:
                                        'Tell us which languages are relevant at your centre to complete your Spark Relief Teachers profile.',
                                },
                            },
                        },
                        {
                            path: ECCOnBoardingTabNames.AGE,
                            element: <CentreAges />,
                            handle: {
                                meta: {
                                    title: 'Childcare Onboarding - Age Groups | Spark Relief Teachers',
                                    description:
                                        'Add the age groups your centre supports so educator matching is aligned with your service.',
                                },
                            },
                        },
                        {
                            path: ECCOnBoardingTabNames.HOURS,
                            element: <CentreOpenTimes />,
                            handle: {
                                meta: {
                                    title: 'Childcare Onboarding - Opening Hours | Spark Relief Teachers',
                                    description:
                                        'Enter your centre opening hours so bookings can be matched to your daily schedule.',
                                },
                            },
                        },
                        {
                            path: ECCOnBoardingTabNames.BUSINESS_MANAGER,
                            element: <CentreBusinessManager />,
                            handle: {
                                meta: {
                                    title: 'Childcare Onboarding - Business Manager Details | Spark Relief Teachers',
                                    description:
                                        'Add your business manager details to support communication and centre account setup in Spark Relief Teachers.',
                                },
                            },
                        },
                        {
                            path: ECCOnBoardingTabNames.SUBMIT,
                            element: <CentreOnboardingSubmit />,
                            handle: {
                                meta: {
                                    title: 'Childcare Onboarding - Review and Submit | Spark Relief Teachers',
                                    description:
                                        'Review your childcare onboarding details and submit your profile to finish setting up Spark Relief Teachers.',
                                },
                            },
                        },
                    ],
                },
            ],
            handle: {
                meta: {
                    title: 'Childcare Onboarding | Spark Relief Teachers',
                    description:
                        'Complete your centre profile to start booking qualified early childhood educators through Spark Relief Teachers.',
                },
            },
        },
    ],
}

export default onboardingRoutes
